import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { Star, MapPin, Phone, Clock, ExternalLink, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_SLUGS = ["portici", "napoli", "pomigliano"];

export default function SedePage() {
  const { slug } = useParams();
  const [sede, setSede] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) { setError(true); setLoading(false); return; }
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/gbp/sede/${slug}`);
        if (mounted) setSede(r.data);
      } catch {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  if (!VALID_SLUGS.includes(slug)) return <Navigate to="/" replace />;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid={`sede-page-${slug}`}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-[#163321]" />
          </div>
        ) : error || !sede ? (
          <div className="max-w-2xl mx-auto px-5 py-16 text-center">
            <p className="text-zinc-500 mb-4">Informazioni sede non disponibili al momento.</p>
            <Link to="/" className="text-[#163321] hover:underline">← Torna alla home</Link>
          </div>
        ) : (
          <>
            {/* JSON-LD InsuranceAgency per sede specifica */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "InsuranceAgency",
                  "@id": `https://www.colucciassicuratori.com/sedi/${slug}#location`,
                  name: `Colucci Assicuratori — ${sede.label}`,
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: sede.address_line,
                    addressLocality: sede.city,
                    addressRegion: sede.province,
                    postalCode: sede.postal_code,
                    addressCountry: "IT",
                  },
                  geo: { "@type": "GeoCoordinates", latitude: sede.lat, longitude: sede.lng },
                  telephone: sede.phone,
                  url: `https://www.colucciassicuratori.com/sedi/${slug}`,
                  ...(sede.review_count > 0 && {
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: sede.rating,
                      reviewCount: sede.review_count,
                      bestRating: 5,
                      worstRating: 1,
                    },
                  }),
                }),
              }}
            />

            {/* Hero sede */}
            <section className="bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white">
              <div className="max-w-5xl mx-auto px-5 sm:px-6 py-12 sm:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white mb-6">
                  <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
                </Link>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D97B58] font-semibold mb-2">Sede di</p>
                <h1 className="font-heading text-3xl sm:text-5xl font-medium tracking-tight mb-3" data-testid="sede-title">
                  Colucci Assicuratori<br />
                  <span className="text-[#D97B58]">{sede.label}</span>
                </h1>
                <p className="text-sm sm:text-base text-white/70 max-w-xl">
                  {sede.formatted_address}
                </p>
                {sede.review_count > 0 && (
                  <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full" data-testid="sede-rating-badge">
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(sede.rating) ? 'text-amber-400 fill-amber-400' : 'text-white/30'}`} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold">{sede.rating}</span>
                    <span className="text-xs text-white/60">· {sede.review_count} recensioni Google</span>
                  </div>
                )}
              </div>
            </section>

            {/* Info card grid */}
            <section className="max-w-5xl mx-auto px-5 sm:px-6 py-12">
              <div className="grid md:grid-cols-3 gap-4">
                {/* Indirizzo */}
                <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5" data-testid="sede-address">
                  <div className="w-10 h-10 rounded-xl bg-[#163321]/5 text-[#163321] flex items-center justify-center mb-3">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <p className="text-xs uppercase tracking-wider text-[#D97B58] font-semibold mb-1">Indirizzo</p>
                  <p className="text-sm font-medium text-[#0F1F14]">{sede.address_line}</p>
                  <p className="text-sm text-[#4A5E51]">{sede.postal_code} {sede.city} ({sede.province})</p>
                  {sede.google_maps_url && (
                    <a href={sede.google_maps_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-xs text-[#163321] hover:underline">
                      Apri in Google Maps <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>

                {/* Telefono */}
                {sede.phone && (
                  <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5" data-testid="sede-phone">
                    <div className="w-10 h-10 rounded-xl bg-[#D97B58]/10 text-[#D97B58] flex items-center justify-center mb-3">
                      <Phone className="w-5 h-5" />
                    </div>
                    <p className="text-xs uppercase tracking-wider text-[#D97B58] font-semibold mb-1">Telefono</p>
                    <a href={`tel:${sede.phone.replace(/\s/g,'')}`} className="text-sm font-medium text-[#0F1F14] hover:text-[#163321]">
                      {sede.phone}
                    </a>
                    <p className="text-[10px] text-[#4A5E51] mt-2">Risposta entro 15 minuti negli orari d&apos;ufficio</p>
                  </div>
                )}

                {/* Stato apertura */}
                <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5" data-testid="sede-status">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                    sede.open_now === true ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {sede.open_now === true ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                  </div>
                  <p className="text-xs uppercase tracking-wider text-[#D97B58] font-semibold mb-1">Stato</p>
                  <p className="text-sm font-medium text-[#0F1F14]">
                    {sede.open_now === true ? 'Aperto ora' : sede.open_now === false ? 'Chiuso ora' : 'Stato non disponibile'}
                  </p>
                  <p className="text-[10px] text-[#4A5E51] mt-2">Aggiornato in tempo reale da Google</p>
                </div>
              </div>

              {/* Orari */}
              {sede.opening_hours_text?.length > 0 && (
                <div className="mt-6 bg-white border border-[#E5E2DB] rounded-2xl p-6" data-testid="sede-hours">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-[#163321]" />
                    <h2 className="font-heading text-lg font-medium text-[#0F1F14]">Orari di apertura</h2>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                    {sede.opening_hours_text.map((line, i) => {
                      const [day, ...rest] = line.split(":");
                      return (
                        <li key={i} className="flex items-baseline gap-2 py-1.5 border-b border-[#E5E2DB]/60 last:border-0">
                          <span className="font-medium text-[#0F1F14] capitalize w-24 shrink-0">{day}</span>
                          <span className="text-[#4A5E51]">{rest.join(":").trim()}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Mappa Google embed */}
              {sede.lat && sede.lng && (
                <div className="mt-6 bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden" data-testid="sede-map">
                  <iframe
                    title={`Mappa sede ${sede.label}`}
                    width="100%"
                    height="350"
                    loading="lazy"
                    src={`https://maps.google.com/maps?q=${sede.lat},${sede.lng}&z=16&output=embed`}
                    style={{ border: 0 }}
                    allowFullScreen
                  />
                </div>
              )}
            </section>

            {/* Recensioni della sede */}
            <GoogleReviewsCarousel limit={6} sedeSlug={slug} showHeader={true} />

            {/* CTA */}
            <section className="bg-[#F5F4F0] py-12">
              <div className="max-w-2xl mx-auto px-5 sm:px-6 text-center">
                <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F1F14] mb-3">
                  Vieni a trovarci nella sede di {sede.label}
                </h2>
                <p className="text-sm text-[#4A5E51] mb-6">
                  Prenota una consulenza personalizzata: ti seguiremo passo dopo passo nella scelta della copertura giusta.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link to="/consulenza" className="inline-flex items-center gap-2 px-6 h-12 bg-[#163321] text-white rounded-full text-sm font-medium hover:bg-[#224A32] transition-colors" data-testid="sede-cta-consulenza">
                    Prenota consulenza
                  </Link>
                  {sede.phone && (
                    <a href={`tel:${sede.phone.replace(/\s/g,'')}`} className="inline-flex items-center gap-2 px-6 h-12 bg-white border border-[#163321]/20 text-[#163321] rounded-full text-sm font-medium hover:bg-[#163321]/5 transition-colors" data-testid="sede-cta-call">
                      <Phone className="w-4 h-4" /> Chiama ora
                    </a>
                  )}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
